import React, { useState, useEffect, useRef } from 'react';
import { db, auth, storage } from '../firebase';
import {
  collection, onSnapshot, addDoc, updateDoc, deleteDoc, doc, getDocs, serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytes, deleteObject, getDownloadURL } from 'firebase/storage';

const PROCESS_FILE_URL = 'https://us-central1-core6-marketing.cloudfunctions.net/processUploadedFile';
const REPORT_SUMMARY_URL = 'https://us-central1-core6-marketing.cloudfunctions.net/generateReportSummary';
const PULL_GSC_URL = 'https://us-central1-core6-marketing.cloudfunctions.net/pullGscData';
const GSC_OAUTH_START_URL = 'https://us-central1-core6-marketing.cloudfunctions.net/gscOAuthStart';
const LOOKUP_LISTINGS_URL = 'https://us-central1-core6-marketing.cloudfunctions.net/lookupLocalListings';

// The named document slots shown as a checklist on every client profile.
const DOC_TYPES = [
  { value: 'marketing-report', label: 'Marketing Report' },
  { value: 'brand-kit',        label: 'Brand Kit' },
  { value: 'service-pages',    label: 'Service Pages' },
  { value: 'insights-report',  label: 'Insights Report' },
  { value: 'call-clarity',     label: 'Call Clarity' },
  { value: 'forms-clarity',    label: 'Forms Clarity' },
  { value: 'reviews-clarity',  label: 'Reviews Clarity' },
  // Staff-authored customer persona profiles (markdown), built from the
  // client's own Call Clarity + Forms data. Feeds blog topic selection and
  // talking points in generateOutrankItems; read via the MCP get_client_personas.
  { value: 'personas',         label: 'Personas' },
  // Written automatically by chatVocRollup (functions) on the 2nd of each month:
  // the last 90 days of website chat questions, visitor side only, anonymized.
  // Manual uploads are allowed too and are never touched by the automation.
  { value: 'chat-clarity',     label: 'Chat Questions' },
  { value: 'other',            label: 'Other' },
];

const ACCEPTED_EXTENSIONS = ['pdf', 'csv', 'xlsx', 'xls', 'txt', 'docx', 'md'];

// Ceiling on the Brand Voice & Tone field. Client-side only — nothing caps
// brandVoice server-side, and the generators inject it in full.
const BRAND_VOICE_MAX = 12000;

function fmtSize(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function fmtDate(ts) {
  if (!ts) return '';
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return ''; }
}

export default function ContentData({ clientId, client }) {
  const [files, setFiles] = useState([]);
  const [uploadingTo, setUploadingTo] = useState(null); // doc-type value currently uploading
  const [uploadError, setUploadError] = useState('');
  const [dragOverSlot, setDragOverSlot] = useState(null);
  const [expandedSlots, setExpandedSlots] = useState({});
  const [brandVoice, setBrandVoice] = useState(client.brandVoice || '');
  const [summarizingId, setSummarizingId] = useState(null);
  const [pullingGsc, setPullingGsc] = useState(false);
  const [disconnectingGsc, setDisconnectingGsc] = useState(false);
  const [gscMessage, setGscMessage] = useState(null); // { ok, text }
  const [summaryError, setSummaryError] = useState('');
  const [openSummaryId, setOpenSummaryId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [preview, setPreview] = useState(null); // { file, loading, url, text, error }
  const [savingVoice, setSavingVoice] = useState(false);
  const [voiceSaved, setVoiceSaved] = useState(false);
  const [localPlaceId, setLocalPlaceId] = useState(client.localPlaceId || '');
  const [localScanPoint, setLocalScanPoint] = useState(client.localScanPoint || '');
  const [savingLocal, setSavingLocal] = useState(false);
  const [localSaved, setLocalSaved] = useState(false);
  const [localError, setLocalError] = useState('');
  const [lookupQuery, setLookupQuery] = useState(client.businessName || '');
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupResults, setLookupResults] = useState(null);
  const [lookupError, setLookupError] = useState('');
  const fileInputRef = useRef(null);
  const pendingCategoryRef = useRef(null);
  const loadedRef = useRef(false);

  // Hydrate the textarea once from the client doc (don't clobber in-progress edits)
  useEffect(() => {
    if (!loadedRef.current && client) {
      setBrandVoice(client.brandVoice || '');
      loadedRef.current = true;
    }
  }, [client]);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'clients', clientId, 'files'), snap => {
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      all.sort((a, b) => (b.uploadedAt?.seconds || 0) - (a.uploadedAt?.seconds || 0));
      setFiles(all);
    });
    return unsub;
  }, [clientId]);

  const triggerExtraction = async (fileDocId) => {
    const idToken = await auth.currentUser?.getIdToken();
    fetch(PROCESS_FILE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
      body: JSON.stringify({ clientId, fileDocId }),
    }).catch(err => console.error('processUploadedFile call failed:', err));
  };

  const handleFiles = async (fileList, category) => {
    setUploadError('');
    const picked = Array.from(fileList || []);
    if (picked.length === 0 || !category) return;

    const invalid = picked.find(f => !ACCEPTED_EXTENSIONS.includes((f.name.split('.').pop() || '').toLowerCase()));
    if (invalid) {
      setUploadError(`"${invalid.name}" is not a supported type (PDF, CSV, XLSX, TXT, DOCX, MD).`);
      return;
    }

    setUploadingTo(category);
    try {
      for (const file of picked) {
        const safeName = file.name.replace(/[^\w.\- ]+/g, '_');
        const storagePath = `clients/${clientId}/uploads/${category}/${Date.now()}_${safeName}`;
        await uploadBytes(ref(storage, storagePath), file);

        const metaDoc = await addDoc(collection(db, 'clients', clientId, 'files'), {
          filename: file.name,
          category,
          storagePath,
          contentType: file.type || '',
          size: file.size,
          uploadedAt: serverTimestamp(),
          uploadedBy: auth.currentUser?.email || 'unknown',
          processingStatus: 'pending',
        });
        triggerExtraction(metaDoc.id);
      }
    } catch (e) {
      console.error('Upload failed:', e);
      setUploadError('Upload failed: ' + (e.message || 'unknown error'));
    } finally {
      setUploadingTo(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const openPickerFor = (category) => {
    pendingCategoryRef.current = category;
    fileInputRef.current?.click();
  };

  const deleteFile = async (file) => {
    if (!window.confirm(`Delete "${file.filename}"? This removes the file and its extracted text.`)) return;
    try {
      await deleteObject(ref(storage, file.storagePath)).catch(() => {}); // tolerate already-gone object
      if (file.sidecarPath) await deleteObject(ref(storage, file.sidecarPath)).catch(() => {});
      await deleteDoc(doc(db, 'clients', clientId, 'files', file.id));
    } catch (e) {
      console.error('Delete failed:', e);
      alert('Delete failed. Please try again.');
    }
  };

  const retryProcessing = async (file) => {
    await updateDoc(doc(db, 'clients', clientId, 'files', file.id), { processingStatus: 'pending', processingError: null });
    triggerExtraction(file.id);
  };

  const generateSummary = async (file) => {
    setSummaryError('');
    setSummarizingId(file.id);
    try {
      const idToken = await auth.currentUser?.getIdToken();
      const res = await fetch(REPORT_SUMMARY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ clientId, fileDocId: file.id }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Summary generation failed');
      setOpenSummaryId(file.id); // snapshot listener delivers the saved summary
    } catch (e) {
      console.error('generateSummary failed:', e);
      setSummaryError(e.message);
    } finally {
      setSummarizingId(null);
    }
  };

  const copySummary = async (file) => {
    try {
      await navigator.clipboard.writeText(file.clientSummary || '');
      setCopiedId(file.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      alert('Copy failed — select the text and copy manually.');
    }
  };

  const pullGsc = async () => {
    setGscMessage(null);
    setPullingGsc(true);
    try {
      const idToken = await auth.currentUser?.getIdToken();
      const res = await fetch(PULL_GSC_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ clientId }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.details || data.error || 'GSC pull failed');
      setGscMessage({ ok: true, text: `✓ Pulled ${data.queries} queries and ${data.blogPosts} blog post${data.blogPosts !== 1 ? 's' : ''} from ${data.property}` });
    } catch (e) {
      setGscMessage({ ok: false, text: e.message });
    } finally {
      setPullingGsc(false);
    }
  };

  // Disconnect GSC for this client: delete the stored pulls (so they stop
  // feeding blog-topic generation right away) and set gscDisabled so the
  // monthly cron won't silently re-discover and reconnect the property.
  const disconnectGsc = async () => {
    if (!window.confirm(
      'Disconnect Search Console for this client?\n\n' +
      'This deletes the stored GSC pulls so they stop feeding blog-topic ' +
      'generation, and stops the monthly auto-pull. You can reconnect anytime.'
    )) return;
    setGscMessage(null);
    setDisconnectingGsc(true);
    try {
      const gscSnap = await getDocs(collection(db, 'clients', clientId, 'gsc'));
      await Promise.all(gscSnap.docs.map(d => deleteDoc(doc(db, 'clients', clientId, 'gsc', d.id))));
      // Also drop the trailing-28-day blog overview cache (getBlogGscOverview).
      // It is already gated by gscDisabled server-side; this just stops it sitting stale.
      await deleteDoc(doc(db, 'clients', clientId, 'gscBlog', 'recent')).catch(() => {});
      await updateDoc(doc(db, 'clients', clientId), {
        gscProperty: null,
        gscLastPulledAt: null,
        gscError: null,
        gscDisabled: true,
      });
      setGscMessage({ ok: true, text: `✓ Disconnected — removed ${gscSnap.size} stored pull${gscSnap.size !== 1 ? 's' : ''}; GSC will no longer feed topic generation.` });
    } catch (e) {
      setGscMessage({ ok: false, text: e.message });
    } finally {
      setDisconnectingGsc(false);
    }
  };

  // Re-enable GSC. Clears the disabled flag; the normal Connect / Pull Now
  // flow takes over and the monthly cron resumes auto-pulling.
  const reconnectGsc = async () => {
    setGscMessage(null);
    try {
      await updateDoc(doc(db, 'clients', clientId), { gscDisabled: false });
      setGscMessage({ ok: true, text: '✓ Re-enabled — use Connect Google / Pull Now to pull fresh data.' });
    } catch (e) {
      setGscMessage({ ok: false, text: e.message });
    }
  };

  // ---- File preview / download ----
  const ext = (name) => (name || '').split('.').pop().toLowerCase();
  const previewKind = (name) => {
    const e = ext(name);
    if (e === 'pdf') return 'pdf';
    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(e)) return 'image';
    if (['txt', 'md', 'csv'].includes(e)) return 'text';
    return 'none'; // docx, xlsx, xls — no in-browser preview
  };

  const openPreview = async (f) => {
    setPreview({ file: f, loading: true, url: null, text: null, error: null });
    try {
      const url = await getDownloadURL(ref(storage, f.storagePath));
      const kind = previewKind(f.filename);
      if (kind === 'text') {
        const res = await fetch(url);
        const text = await res.text();
        setPreview({ file: f, loading: false, url, text, error: null });
      } else {
        setPreview({ file: f, loading: false, url, text: null, error: null });
      }
    } catch (e) {
      console.error('Preview failed:', e);
      setPreview({ file: f, loading: false, url: null, text: null, error: e.message || 'Could not load file' });
    }
  };

  const downloadFile = async (f) => {
    try {
      const url = await getDownloadURL(ref(storage, f.storagePath));
      const a = document.createElement('a');
      a.href = url;
      a.download = f.filename || 'download';
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      console.error('Download failed:', e);
      alert('Could not download this file. Please try again.');
    }
  };

  const saveBrandVoice = async () => {
    setSavingVoice(true);
    try {
      // Stamped so get_client_brand_kit can tell whether these notes predate or
      // postdate the uploaded Brand Kit document — the two drift and nothing
      // else records when the notes were last touched.
      await updateDoc(doc(db, 'clients', clientId), { brandVoice, brandVoiceUpdatedAt: serverTimestamp() });
      setVoiceSaved(true);
      setTimeout(() => setVoiceSaved(false), 2500);
    } catch (e) {
      console.error('Failed to save brand voice:', e);
      alert('Save failed. Please try again.');
    } finally {
      setSavingVoice(false);
    }
  };

  // Google Maps rank tracking is opt-in per client — both fields must be set
  // (or both cleared) for keyword follow-ups to capture a local pack snapshot.
  const saveLocalConfig = async () => {
    setLocalError('');
    const placeId = localPlaceId.trim();
    const scanPoint = localScanPoint.trim();
    if (scanPoint && !/^-?\d+(\.\d+)?,\s*-?\d+(\.\d+)?(,\s*\d+z)?$/.test(scanPoint)) {
      setLocalError('Scan point must be "lat,lng" (e.g. 36.6002,-121.8947) — copy it from a Google Maps right-click "What\'s here?"');
      return;
    }
    if ((placeId && !scanPoint) || (!placeId && scanPoint)) {
      setLocalError('Set both the Place ID and the scan point together — Maps tracking needs both to run.');
      return;
    }
    setSavingLocal(true);
    try {
      await updateDoc(doc(db, 'clients', clientId), { localPlaceId: placeId, localScanPoint: scanPoint });
      setLocalSaved(true);
      setTimeout(() => setLocalSaved(false), 2500);
    } catch (e) {
      console.error('Failed to save local tracking config:', e);
      setLocalError('Save failed. Please try again.');
    } finally {
      setSavingLocal(false);
    }
  };

  // Setup helper: query live Google Maps listings at the scan point so staff can
  // pick the client's correct place_id/cid — the id the tracker matches against.
  const runLookup = async () => {
    setLookupError('');
    const scanPoint = localScanPoint.trim();
    if (!scanPoint) { setLookupError('Enter the Scan Point (lat,lng) first — the lookup searches at that location.'); return; }
    const q = (lookupQuery.trim() || client.businessName || '').trim();
    if (!q) { setLookupError('Enter a business name or keyword to search.'); return; }
    setLookupBusy(true); setLookupResults(null);
    try {
      const token = await auth.currentUser.getIdToken();
      const res = await fetch(LOOKUP_LISTINGS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ keyword: q, scanPoint }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setLookupResults(data.listings || []);
      if (!(data.listings || []).length) setLookupError('No listings returned — try the exact business name, or re-check the scan point.');
    } catch (e) {
      setLookupError('Lookup failed: ' + e.message);
    } finally {
      setLookupBusy(false);
    }
  };

  const renderFileRow = (f, compact) => {
    const status = f.processingStatus;
    const isReport = f.category === 'marketing-report';
    const summaryOpen = openSummaryId === f.id;
    return (
      <div key={f.id}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: compact ? '4px 0' : '2px 0' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <span style={{ fontSize: 12, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
              {f.filename}
              <span style={{ color: 'var(--text-muted)', marginLeft: 6 }}>{fmtDate(f.uploadedAt)} · {fmtSize(f.size)}</span>
            </span>
            {status === 'failed' && f.processingError && (
              <span style={{ fontSize: 11, color: '#f87171' }}>{f.processingError}</span>
            )}
          </div>
          {status === 'pending' && <span style={{ fontSize: 11, color: '#facc15', whiteSpace: 'nowrap' }}>Processing…</span>}
          {status === 'failed' && (
            <button className="btn btn-sm btn-secondary" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => retryProcessing(f)}>Retry</button>
          )}
          {isReport && status === 'complete' && (
            f.clientSummary ? (
              <button
                className="btn btn-sm btn-secondary"
                style={{ fontSize: 11, padding: '2px 8px', color: '#4ade80', borderColor: '#16a34a55' }}
                onClick={() => setOpenSummaryId(summaryOpen ? null : f.id)}
              >
                {summaryOpen ? 'Hide Summary' : '✓ Summary'}
              </button>
            ) : (
              <button
                className="btn btn-sm btn-secondary"
                style={{ fontSize: 11, padding: '2px 8px' }}
                disabled={summarizingId !== null}
                onClick={() => generateSummary(f)}
              >
                {summarizingId === f.id ? 'Generating…' : 'Generate Summary'}
              </button>
            )
          )}
          <button className="btn btn-sm btn-secondary" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => openPreview(f)}>View</button>
          <button className="btn btn-sm btn-danger" style={{ opacity: 0.6, fontSize: 11, padding: '2px 7px' }} onClick={() => deleteFile(f)}>🗑</button>
        </div>

        {/* Client-facing summary email panel (marketing reports only) */}
        {isReport && summaryOpen && f.clientSummary && (
          <div style={{ margin: '6px 0 8px', padding: '12px 14px', borderRadius: 8, background: 'var(--bg-primary)', border: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Client Summary Email {f.summaryGeneratedAt && <span style={{ fontWeight: 400, color: 'var(--text-muted)', textTransform: 'none' }}>· {fmtDate(f.summaryGeneratedAt)}</span>}
              </span>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  className="btn btn-sm btn-primary"
                  style={{ fontSize: 11, padding: '3px 10px' }}
                  onClick={() => copySummary(f)}
                >
                  {copiedId === f.id ? '✓ Copied' : 'Copy'}
                </button>
                <button
                  className="btn btn-sm btn-secondary"
                  style={{ fontSize: 11, padding: '3px 10px' }}
                  disabled={summarizingId !== null}
                  onClick={() => generateSummary(f)}
                >
                  {summarizingId === f.id ? 'Generating…' : 'Regenerate'}
                </button>
              </div>
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--text-primary)', lineHeight: 1.65, whiteSpace: 'pre-wrap', maxHeight: 360, overflowY: 'auto' }}>
              {f.clientSummary}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="card">
      <div style={{ marginBottom: 18 }}>
        <div className="card-title" style={{ marginBottom: 0 }}>Content Data</div>
      </div>

      {/* Document checklist — one named slot per doc type */}
      <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', marginBottom: 16 }}>
        {DOC_TYPES.map((docType, i) => {
          const slotFiles = files.filter(f => f.category === docType.value);
          const latest = slotFiles[0];
          const older = slotFiles.slice(1);
          const expanded = !!expandedSlots[docType.value];
          const isDragTarget = dragOverSlot === docType.value;
          return (
            <div
              key={docType.value}
              onDragOver={e => { e.preventDefault(); setDragOverSlot(docType.value); }}
              onDragLeave={() => setDragOverSlot(s => (s === docType.value ? null : s))}
              onDrop={e => { e.preventDefault(); setDragOverSlot(null); handleFiles(e.dataTransfer.files, docType.value); }}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderTop: i === 0 ? 'none' : '1px solid var(--border)', background: isDragTarget ? '#0a1f12' : 'transparent', transition: 'background 0.15s' }}
            >
              <span style={{ width: 18, textAlign: 'center', fontSize: 13, flexShrink: 0 }}>
                {latest ? <span style={{ color: '#4ade80' }}>✓</span> : <span style={{ color: 'var(--text-muted)' }}>—</span>}
              </span>
              <div style={{ width: 130, flexShrink: 0, fontSize: 13, fontWeight: 600, color: latest ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                {docType.label}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                {latest ? (
                  <>
                    {renderFileRow(latest, false)}
                    {older.length > 0 && (
                      <button
                        onClick={() => setExpandedSlots(s => ({ ...s, [docType.value]: !expanded }))}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--text-muted)', padding: '2px 0' }}
                      >
                        {expanded ? '▾ hide' : `▸ ${older.length} older file${older.length > 1 ? 's' : ''}`}
                      </button>
                    )}
                    {expanded && older.map(f => renderFileRow(f, true))}
                  </>
                ) : (
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Not uploaded</span>
                )}
              </div>
              <button
                className="btn btn-sm btn-secondary"
                style={{ flexShrink: 0, fontSize: 11 }}
                disabled={uploadingTo !== null}
                onClick={() => openPickerFor(docType.value)}
              >
                {uploadingTo === docType.value ? 'Uploading…' : 'Upload'}
              </button>
            </div>
          );
        })}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.csv,.xlsx,.xls,.txt,.docx,.md"
        style={{ display: 'none' }}
        onChange={e => handleFiles(e.target.files, pendingCategoryRef.current)}
      />
      {uploadError && <div style={{ fontSize: 12, color: '#f87171', marginBottom: 10 }}>{uploadError}</div>}
      {summaryError && <div style={{ fontSize: 12, color: '#f87171', marginBottom: 10 }}>Summary failed: {summaryError}</div>}
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: -8, marginBottom: 16 }}>
        PDF, CSV, XLSX, TXT, DOCX, MD — drag a file onto a row or click Upload
      </div>

      {/* Google Search Console connection */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-primary)', marginBottom: 16, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Search Console</span>
        {client.gscDisabled ? (
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Disconnected — not pulling and not feeding topic generation</span>
        ) : client.gscProperty ? (
          <span style={{ fontSize: 12, color: '#4ade80' }}>
            ✓ {client.gscProperty.replace('sc-domain:', '')}
            {client.gscLastPulledAt && <span style={{ color: 'var(--text-muted)' }}> · pulled {fmtDate(client.gscLastPulledAt)}</span>}
          </span>
        ) : (
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Not connected — pulls automatically on the 2nd of each month once linked</span>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          {client.gscDisabled ? (
            <button
              className="btn btn-sm btn-secondary"
              style={{ fontSize: 11 }}
              onClick={reconnectGsc}
              title="Re-enable Search Console for this client (resumes the monthly auto-pull)"
            >
              Reconnect
            </button>
          ) : (
            <>
              <button
                className="btn btn-sm btn-secondary"
                style={{ fontSize: 11 }}
                onClick={async () => {
                  const idToken = await auth.currentUser?.getIdToken();
                  window.open(`${GSC_OAUTH_START_URL}?token=${encodeURIComponent(idToken || '')}`, '_blank');
                }}
                title="One-time: authorize the hub with the Google account that owns all client GSC properties"
              >
                Connect Google
              </button>
              <button
                className="btn btn-sm btn-secondary"
                style={{ fontSize: 11 }}
                disabled={pullingGsc}
                onClick={pullGsc}
              >
                {pullingGsc ? 'Pulling…' : 'Pull Now'}
              </button>
              {client.gscProperty && (
                <button
                  className="btn btn-sm btn-secondary"
                  style={{ fontSize: 11 }}
                  disabled={disconnectingGsc}
                  onClick={disconnectGsc}
                  title="Delete stored GSC pulls and stop the monthly auto-pull for this client"
                >
                  {disconnectingGsc ? 'Disconnecting…' : 'Disconnect'}
                </button>
              )}
            </>
          )}
        </div>
        {(gscMessage || client.gscError) && (
          <div style={{ width: '100%', fontSize: 11, color: gscMessage?.ok ? '#4ade80' : '#f87171' }}>
            {gscMessage ? gscMessage.text : `Last pull failed: ${client.gscError}`}
          </div>
        )}
      </div>

      {/* Local Pack (Google Maps) rank tracking — optional, feeds Keyword Follow-Ups */}
      <div style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-primary)', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Local Pack Tracking</span>
          {client.localPlaceId && client.localScanPoint ? (
            <span style={{ fontSize: 12, color: '#4ade80' }}>✓ Configured — Keyword Follow-Ups will capture Maps rank</span>
          ) : (
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Not set up — Keyword Follow-Ups will skip Maps rank for this client</span>
          )}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10, marginBottom: 8 }}>
          <div className="form-field" style={{ marginBottom: 0 }}>
            <label>Google Place ID</label>
            <input
              type="text"
              placeholder="ChIJP0warDDowokRfvXMkBqL5Cg"
              value={localPlaceId}
              onChange={e => setLocalPlaceId(e.target.value)}
              disabled={savingLocal}
              style={{ fontSize: 13 }}
            />
          </div>
          <div className="form-field" style={{ marginBottom: 0 }}>
            <label>Scan Point <span style={{ color: 'var(--text-muted)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>— lat,lng</span></label>
            <input
              type="text"
              placeholder="36.6002,-121.8947"
              value={localScanPoint}
              onChange={e => setLocalScanPoint(e.target.value)}
              disabled={savingLocal}
              style={{ fontSize: 13 }}
            />
          </div>
        </div>
        {/* Setup helper — look up the client's live Maps listing so the stored
            id is guaranteed to match what the tracker matches against. */}
        <div style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Business name or keyword to search"
              value={lookupQuery}
              onChange={e => setLookupQuery(e.target.value)}
              disabled={lookupBusy}
              style={{ flex: '1 1 220px', fontSize: 13, padding: '6px 8px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)' }}
            />
            <button className="btn btn-sm btn-secondary" onClick={runLookup} disabled={lookupBusy}>
              {lookupBusy ? 'Searching…' : 'Find Place ID'}
            </button>
          </div>
          {lookupError && <div style={{ fontSize: 11, color: '#f87171', marginTop: 6 }}>{lookupError}</div>}
          {lookupResults && lookupResults.length > 0 && (
            <div style={{ marginTop: 8, border: '1px solid var(--border)', borderRadius: 6, maxHeight: 260, overflowY: 'auto' }}>
              {lookupResults.map((r, idx) => {
                const selected = !!localPlaceId.trim() && (r.placeId === localPlaceId.trim() || r.cid === localPlaceId.trim());
                return (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderBottom: '1px solid var(--border)', background: selected ? 'rgba(74,222,128,0.12)' : 'transparent' }}>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)', width: 24, textAlign: 'right' }}>#{r.rank}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 600 }}>{r.title}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                        {[r.category, r.rating ? `★ ${r.rating}${r.reviews ? ` (${r.reviews})` : ''}` : null, r.address].filter(Boolean).join(' · ')}
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.placeId || r.cid}</div>
                    </div>
                    <button className="btn btn-sm btn-secondary" onClick={() => setLocalPlaceId(r.placeId || r.cid)}>
                      {selected ? '✓ Using' : 'Use'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10, lineHeight: 1.5 }}>
          <strong>Setup:</strong> set the Scan Point first (right-click the client's location in Google Maps → copy the lat/lng), then click <strong>Find Place ID</strong> and pick their listing — this guarantees the ID matches what the tracker uses, then Save. IDs copied from Google's <a href="https://developers.google.com/maps/documentation/places/web-service/place-id" target="_blank" rel="noreferrer" style={{ color: '#818cf8' }}>Place ID Finder</a> don't always match and can silently fail to track. Leave both blank to skip Maps rank tracking.
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-sm btn-secondary" onClick={saveLocalConfig} disabled={savingLocal}>
            {savingLocal ? 'Saving…' : 'Save Local Tracking'}
          </button>
          {localSaved && <span style={{ fontSize: 12, color: '#4ade80', fontWeight: 600 }}>✓ Saved</span>}
          {localError && <span style={{ fontSize: 12, color: '#f87171' }}>{localError}</span>}
        </div>
      </div>

      {/* Brand Voice & Tone (moved here from Content Settings) */}
      {/* 12000, raised from 8000: an approved brand document is the best possible
          content for this field, and pasting one verbatim is what keeps the field
          and the document from drifting. Coastal Pipeline's revised doc ran 8135
          chars of body — the old cap forced a hand-trimmed derivative, which is
          the exact thing that caused the drift in the first place. */}
      <div className="form-field">
        <label style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Brand Voice & Tone</span>
          <span style={{ fontSize: 11, color: brandVoice.length > BRAND_VOICE_MAX - 500 ? '#f87171' : 'var(--text-muted)', fontWeight: 400 }}>
            {brandVoice.length} / {BRAND_VOICE_MAX}
          </span>
        </label>
        <textarea
          placeholder={"Describe everything Claude needs to write for this client:\n• Who they are and how long they've been in business\n• Their services and specialties\n• Target audience and locations served\n• Brand tone (formal/casual, bilingual, etc.)\n• What makes them different from competitors\n• Anything to always or never say in their content"}
          value={brandVoice}
          onChange={e => e.target.value.length <= BRAND_VOICE_MAX && setBrandVoice(e.target.value)}
          style={{ minHeight: 160, fontSize: 13, lineHeight: 1.6 }}
        />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button className="btn btn-primary" onClick={saveBrandVoice} disabled={savingVoice}>
          {savingVoice ? 'Saving...' : 'Save Brand Voice'}
        </button>
        {voiceSaved && <span style={{ fontSize: 12, color: '#4ade80', fontWeight: 600 }}>✓ Saved</span>}
      </div>

      {/* File preview modal */}
      {preview && (
        <div
          onClick={() => setPreview(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 10, width: 'min(900px, 100%)', maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
              <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {preview.file.filename}
              </span>
              <button className="btn btn-sm btn-primary" style={{ fontSize: 11 }} onClick={() => downloadFile(preview.file)}>Download</button>
              <button className="btn btn-sm btn-secondary" style={{ fontSize: 11 }} onClick={() => setPreview(null)}>Close</button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', background: 'var(--bg-primary)' }}>
              {preview.loading && (
                <div style={{ textAlign: 'center', padding: 48 }}><div className="spinner"></div></div>
              )}
              {!preview.loading && preview.error && (
                <div style={{ padding: 32, textAlign: 'center', color: '#f87171', fontSize: 13 }}>Could not load file: {preview.error}</div>
              )}
              {!preview.loading && !preview.error && previewKind(preview.file.filename) === 'pdf' && (
                <iframe title="preview" src={preview.url} style={{ width: '100%', height: '78vh', border: 'none' }} />
              )}
              {!preview.loading && !preview.error && previewKind(preview.file.filename) === 'image' && (
                <div style={{ padding: 16, textAlign: 'center' }}>
                  <img src={preview.url} alt={preview.file.filename} style={{ maxWidth: '100%', maxHeight: '76vh' }} />
                </div>
              )}
              {!preview.loading && !preview.error && previewKind(preview.file.filename) === 'text' && (
                <pre style={{ margin: 0, padding: 16, fontSize: 12.5, lineHeight: 1.6, color: 'var(--text-primary)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'ui-monospace, Menlo, monospace' }}>
                  {preview.text}
                </pre>
              )}
              {!preview.loading && !preview.error && previewKind(preview.file.filename) === 'none' && (
                <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.6 }}>
                  This file type ({ext(preview.file.filename).toUpperCase()}) can't be previewed in the browser.<br />
                  Use the Download button above to open it.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
